import { CSSProperties, memo, useMemo } from 'react';
import classNames from 'classnames';

import { TextProps } from './text.types';

import styles from './text.module.css';
import SkeletonRect from '@ui/skeleton-loader/skeleton-rect';

const UnmemoizedText = <T,>(props: TextProps<T>) => {
  const {
    clamp,
    className,
    whiteSpace = 'normal',
    wordBreak,
    tag,
    color,
    hoverColor,
    backgroundColor,
    height,
    minHeight,
    width,
    maxWidth,
    minWidth,
    transform,
    skeletonLoader,
    skeletonLoaderBackground,
    fontSize,
    textShadow,
    transitionDuration = '0.1s',
    opacity,
    ref,
    textAlign = 'left',
    fontFamily,
    style = {},
    id,
    lineHeight,
    border,
    'data-component': dataComponent,
    inheritButtonHover,
    design,
    skLoader,
    children,
    ...rest
  } = props;

  const Tag = tag || 'div';

  /*
    The type becomes too complex for typescript and errors out if trying to properly type the ref in this file.
    Doing it this way at least lets calling `<Text ref={myRef} />` use properly typed ref's in those files.
   */
  const refProp = useMemo(() => (ref ? { ref } : {}) as Record<string, unknown>, [ref]);
  const hasLoader = !!skLoader;

  return (
    <Tag
      {...refProp}
      id={id}
      data-component={dataComponent || 'Text'}
      className={classNames(styles.text, className, {
        [styles.clamp]: !!clamp,
        [styles.skeletonLoader]: !!skeletonLoader,
        [styles.fontSize]: !!fontSize,
        [styles.fontFamily]: !!fontFamily,
        [styles.lineHeight]: !!lineHeight,
        [styles.inheritButtonHover]: !!inheritButtonHover,
        ...(design ? { [design]: design } : {}),
        'can-have-loader': hasLoader,
      })}
      style={
        {
          '--text-color': color,
          '--text-hover-color': hoverColor || color,
          '--text-background-color': backgroundColor,
          '--text-width': width,
          '--text-height': height,
          '--text-min-height': minHeight,
          '--text-max-width': maxWidth,
          '--text-min-width': minWidth,
          '--text-font-size': fontSize,
          '--text-font-family': fontFamily?.includes(' ') ? `'${fontFamily}'` : fontFamily,
          '--text-transition-duration': transitionDuration,
          '--text-opacity': opacity,
          '--text-text-align': textAlign,
          '--text-clamp': clamp,
          ...(transform ? { '--text-transform': transform } : {}),
          ...(wordBreak ? { '--text-word-break': wordBreak } : {}),
          ...(whiteSpace ? { '--text-white-space': whiteSpace } : {}),
          ...(textShadow ? { '--text-text-shadow': textShadow } : {}),
          ...(skeletonLoaderBackground
            ? {
                '--text-skeleton-start': skeletonLoaderBackground.start,
                '--text-skeleton-end': skeletonLoaderBackground.end,
              }
            : {}),
          ...(lineHeight ? { '--text-line-height': lineHeight } : {}),
          ...(border ? { '--text-border': border } : {}),
          ...style,
        } as CSSProperties
      }
      {...rest}
    >
      {children}
      {hasLoader ? <SkeletonRect {...skLoader} /> : null}
    </Tag>
  );
};

const Text = memo(UnmemoizedText) as typeof UnmemoizedText;

export default Text;
